import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MainTemplate } from '../../templates/MainTemplate';
import { Input } from '../../atoms/Input';
import { Button } from '../../atoms/Button';
import { useAccountPage } from './hook';
import { ErrorIcon } from '../../atoms/icons/input/ErrorIcon';
import { changePasswordApi } from '../../../core/http/httpClient';
import { useResponsive } from '../../../hooks/useResponsive';

export const AccountPage = () => {
  const { username, email } = useAccountPage();
  const { isMobile, isTablet } = useResponsive();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // Check if form is valid (all fields filled and passwords match)
  const isFormValid = oldPassword.trim() !== '' &&
    newPassword.trim() !== '' &&
    confirmPassword.trim() !== '' &&
    newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) {
      return;
    }
    if (newPassword !== confirmPassword) {
      setSubmitMessage({
        type: 'error',
        message: 'New password and confirm password do not match.'
      });
      return;
    }

    setIsLoading(true);
    setSubmitMessage(null);

    try {
      const response = await changePasswordApi.call({
        current_password: oldPassword,
        new_password: newPassword,
      });

      setSubmitMessage({
        type: 'success',
        message: 'Password changed successfully!'
      });

      // Reset form
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      let errorMessage = 'Failed to change password. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData && typeof errorData === 'object') {
          if ('detail' in errorData && errorData.detail) {
            errorMessage = errorData.detail;
          } else if ('message' in errorData && errorData.message) {
            errorMessage = errorData.message;
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSubmitMessage({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainTemplate title="Account">
      <StyledSection $isMobile={isMobile} $isTablet={isTablet}>
        <StyledSectionTitle $isMobile={isMobile}>User Information</StyledSectionTitle>
        <StyledInfoRow $isMobile={isMobile}>
          <StyledInfoLabel $isMobile={isMobile}>Username:</StyledInfoLabel>
          <StyledInfoValue $isMobile={isMobile}>{username}</StyledInfoValue>
        </StyledInfoRow>
        <StyledInfoRow $isMobile={isMobile}>
          <StyledInfoLabel $isMobile={isMobile}>Email address:</StyledInfoLabel>
          <StyledInfoValue $isMobile={isMobile}>{email}</StyledInfoValue>
        </StyledInfoRow>
      </StyledSection>
      <StyledSection $isMobile={isMobile} $isTablet={isTablet}>
        <StyledSectionTitle $isMobile={isMobile}>Reset Password</StyledSectionTitle>
        <form onSubmit={handleSubmit} autoComplete="off">
          <StyledInputGroup $isMobile={isMobile} $isTablet={isTablet}>
            <Input
              label="Old Password"
              name="oldPassword"
              type="password"
              value={oldPassword}
              onChange={(value) => {
                setOldPassword(value);
                setSubmitMessage(null);
              }}
              placeholder="Old Password"
              required
              fullWidth
            />
            <Input
              label="New Password"
              name="newPassword"
              type="password"
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                setSubmitMessage(null);
              }}
              placeholder="New Password"
              required
              fullWidth
            />
            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                setSubmitMessage(null);
              }}
              placeholder="Verify New Password"
              required
              fullWidth
            />
            {submitMessage && (
              <StyledMessageContainer $type={submitMessage.type} $isMobile={isMobile}>
                {submitMessage.type === 'error' && <ErrorIcon />}
                <span>{submitMessage.message}</span>
              </StyledMessageContainer>
            )}
          </StyledInputGroup>
          <StyledButtonRow $isMobile={isMobile}>
            <Button
              text={isLoading ? 'Resetting...' : 'Reset Password'}
              variant="border"
              type="submit"
              fullWidth
              disabled={isLoading || !isFormValid}
            />
          </StyledButtonRow>
        </form>
      </StyledSection>
    </MainTemplate>
  );
};

const StyledSection = styled.div<{ $isMobile?: boolean; $isTablet?: boolean }>`
  margin-bottom: 30px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  ${({ $isMobile, $isTablet }) => {
    if ($isMobile) {
      return `
        min-width: auto;
        max-width: 100%;
        margin-bottom: 20px;
      `;
    } else if ($isTablet) {
      return `
        min-width: auto;
        max-width: 100%;
        margin-bottom: 25px;
      `;
    } else {
      return `
        min-width: 340px;
        max-width: 600px;
      `;
    }
  }}
`;

const StyledSectionTitle = styled.div<{ $isMobile?: boolean }>`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 24px;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      font-size: 18px;
      margin-bottom: 16px;
      `
      : ""}
`;

const StyledInfoRow = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      margin-bottom: 16px;
      `
      : ""}
`;

const StyledInfoLabel = styled.div<{ $isMobile?: boolean }>`
  font-size: 16px;
  font-weight: 400;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      width: auto;
      font-size: 14px;
      color: #666;
      `
      : `
      width: 140px;
      `}
`;

const StyledInfoValue = styled.div<{ $isMobile?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      font-size: 15px;
      word-break: break-word;
      `
      : ""}
`;

const StyledInputGroup = styled.div<{ $isMobile?: boolean; $isTablet?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  border-radius: 8px;
  
  ${({ $isMobile, $isTablet }) => {
    if ($isMobile) {
      return `
        min-width: auto;
        max-width: 100%;
        gap: 12px;
      `;
    } else if ($isTablet) {
      return `
        min-width: auto;
        max-width: 100%;
      `;
    } else {
      return `
        min-width: 340px;
        max-width: 600px;
      `;
    }
  }}
`;

const StyledButtonRow = styled.div<{ $isMobile?: boolean }>`
  margin-top: 24px;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      margin-top: 20px;
      `
      : ""}
`;

const StyledMessageContainer = styled.div<{ $type: 'success' | 'error'; $isMobile?: boolean }>`
  color: ${({ $type }) => $type === 'success' ? '#1cb954' : '#d91c0b'};
  font-size: 13px;
  margin-top: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: ${({ $type }) => $type === 'success' ? '#f0fdf4' : '#fef2f2'};
  border: 1px solid ${({ $type }) => $type === 'success' ? '#bbf7d0' : '#fecaca'};
  border-radius: 4px;
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      font-size: 12px;
      padding: 6px 10px;
      gap: 4px;
      `
      : ""}
`;